import { fetchAuthSession } from 'aws-amplify/auth';

type AuthSessionSnapshot = {
    tokens?: {
        accessToken?: unknown;
        idToken?: unknown;
    };
};

type AuthSessionFetcher = () => Promise<AuthSessionSnapshot>;

export type RecipientAuthFlow = 'platform-auth' | 'file-mfa';

/**
 * Cognito tokens are the source of truth for whether platform authentication
 * has completed. The MobX user store can lag briefly behind Amplify's signed-in
 * event while the recipient flow advances to its next policy step.
 */
export const hasPlatformSession = async (
    fetchSession: AuthSessionFetcher = fetchAuthSession,
): Promise<boolean> => {
    try {
        const { tokens } = await fetchSession();
        return Boolean(tokens?.accessToken && tokens?.idToken);
    } catch {
        return false;
    }
};

/**
 * The first configured recipient factor obtains the platform session and must
 * therefore use Cognito. Later phone/password policy checks are file-level MFA
 * and must use the IAM-authenticated step endpoints without starting sign-in
 * again.
 */
export const resolveRecipientAuthFlow = (platformSessionExists: boolean): RecipientAuthFlow => (
    platformSessionExists ? 'file-mfa' : 'platform-auth'
);

/**
 * Outcome of probing the backend for what the recipient still has to satisfy.
 * `none` is a definitive "nothing further is required"; `unknown` means the probe
 * could not run (no files to open yet, or an unrecognised failure) and carries no
 * information about the policy.
 */
export type MfaProbeResult = 'none' | 'phone' | 'password' | 'unknown';

export type RecipientPhoneGate = 'skip' | 'password' | 'prompt';

/**
 * Decide what a revisiting recipient sees, given the probe result.
 *
 * Fail closed: only a definitive `none` skips phone verification. `unknown` must
 * prompt, because treating "could not determine" as "already satisfied" leaves the
 * recipient on a locked file with no step to complete.
 */
export const resolvePhoneGate = (
    probe: MfaProbeResult,
    hasPasswordProtection: boolean,
): RecipientPhoneGate => {
    if (probe === 'none') {
        return 'skip';
    }
    if (probe === 'password' && hasPasswordProtection) {
        return 'password';
    }
    return 'prompt';
};
